import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AddressDTO, AddressType } from '@iota/iota-management-portal-v4-client';
import { ModalService } from '@iota/iota-ui-components';
import { AddressTypeDescriptions } from '../../constants-and-enums/address-type';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../../dialogs/dialog-confirmation/dialog-confirmation.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CountryName } from '../../constants-and-enums/country-name';
import { Subject, takeUntil } from 'rxjs';
import { countryCodes } from '../../utils';

export interface AddressUpsertEvent {
    address: AddressDTO;
    callback: (success: boolean) => void;
}

@Component({
    standalone: false,
    selector: 'app-address',
    templateUrl: './address.component.html',
    styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit, OnDestroy {
    @Input() address: AddressDTO;
    @Input() showSaveButton = true;
    @Input() showAddressSubtitle = true;
    @Input() editMode = false;
    @Input() showEditButton = true;

    @Output() delete: EventEmitter<AddressDTO> = new EventEmitter();
    @Output() save: EventEmitter<AddressUpsertEvent> = new EventEmitter();
    @Output() editCancelled: EventEmitter<string> = new EventEmitter();

    addressForm: FormGroup = this.fb.group({
        name1: ['', Validators.required],
        name2: [''],
        street: ['', Validators.required],
        houseNumber: [''],
        street2: [''],
        zipCode: ['', Validators.required],
        city: ['', Validators.required],
        countryCode: ['DE'],
        country: [CountryName.get('DE'), Validators.required]
    });

    countryCodes = countryCodes;
    countryOptions: { key: string; name: string }[] = [];

    protected readonly CountryName = CountryName;
    protected readonly AddressType = AddressType;
    private destroy$ = new Subject<void>();

    constructor(
        private dialog: ModalService,
        private fb: FormBuilder
    ) {}

    ngOnInit(): void {
        this.setupCountryOptions();

        this.addressForm
            .get('countryCode')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateCountry();
            });

        if (this.address) {
            this.addressForm.patchValue({
                name1: this.address.name1,
                name2: this.address.name2,
                street: this.address.street,
                houseNumber: this.address.houseNumber,
                street2: this.address.street2,
                zipCode: this.address.zipCode,
                city: this.address.city,
                countryCode: this.address.countryCode,
                country: this.address.country
            });
            this.updateCountry();
        }
        if (!this.editMode) this.addressForm.disable();
    }

    getAddressTypeDescription(addressType: string): string {
        if (!this.showAddressSubtitle) {
            return AddressType.MAIN_ADDRESS;
        }
        return AddressTypeDescriptions.get(addressType);
    }

    updateCountry() {
        const countryCode = this.addressForm.get('countryCode').value;
        const countryName = CountryName.get(countryCode);
        this.addressForm.patchValue({ country: countryName });
    }

    deleteAddress() {
        const dialogData: ConfirmationDialogData = {
            title: $localize`Delete address`,
            text: $localize`Are you sure you want to delete the address?`,
            cancelButtonStyle: 'primary',
            confirmButtonStyle: 'error'
        };

        const dialogRef = this.dialog.open(DialogConfirmationComponent, {
            data: dialogData
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.delete.emit(this.address);
            }
        });
    }

    cancelEdit() {
        this.editCancelled.emit();
        this.editMode = false;
        this.addressForm.disable();
    }

    saveEdit() {
        this.save.emit({
            address: this.getUpdatedAddress(),
            callback: (success) => {
                if (success) {
                    this.editMode = false;
                    this.addressForm.disable();
                }
            }
        });
    }

    getUpdatedAddress(): AddressDTO {
        if (this.addressForm.valid) {
            return {
                ...this.address,
                ...this.addressForm.value
            };
        }

        return null;
    }

    handleEditMode() {
        this.editMode = true;
        this.addressForm.enable();
    }

    setupCountryOptions() {
        this.countryOptions = this.countryCodes.map((code) => ({
            key: code,
            name: CountryName.get(code)
        }));
    }

    onCountryChange(key: string) {
        this.addressForm.patchValue({ countryCode: key });
        this.updateCountry();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
